// src/services/seguimientoExpedientesService.js
// Servicio de Seguimiento de Expedientes ANSES.
// Habla con el backend de GestionEJP (apiClient -> /api), que a su vez junta los
// datos del microservicio servicioAnses con el nombre del cliente.

import apiClient from './apiClient';

const BASE = '/seguimiento-expedientes';

export async function listarExpedientes() {
    try {
        const { data } = await apiClient.get(BASE);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        return [];
    }
}

export async function obtenerExpedientesPorCliente(cuil) {
    try {
        const { data } = await apiClient.get(`${BASE}/cliente/${cuil}`);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        return [];
    }
}

// Lista global — la misma para todos los usuarios.
export async function listarLista() {
    try {
        const { data } = await apiClient.get(`${BASE}/lista`);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        return [];
    }
}

// clave y prioridad son opcionales — sin clave, el backend solo asegura la conexión
// (sin tocar ningún browser); con clave, dispara el intento automático en el mismo paso.
// Agrega el cliente a la lista global — queda visible para todos los usuarios.
export async function agregarALista(cuil, clave, prioridad) {
    await apiClient.post(`${BASE}/lista/${cuil}`, { clave: clave || undefined, prioridad });
}

export async function obtenerPrioridad(cuil) {
    try {
        const { data } = await apiClient.get(`${BASE}/lista/${cuil}/prioridad`);
        return data?.prioridad || 'MEDIA';
    } catch (e) {
        return 'MEDIA';
    }
}

export async function actualizarPrioridad(cuil, prioridad) {
    await apiClient.patch(`${BASE}/lista/${cuil}/prioridad`, { prioridad });
}

// Quita el cliente de la lista global — deja de verse para todos los usuarios.
export async function quitarDeLista(cuil) {
    await apiClient.delete(`${BASE}/lista/${cuil}`);
}

export async function actualizarCliente(cuil) {
    try {
        const { data } = await apiClient.post(`${BASE}/cliente/${cuil}/actualizar`);
        return Array.isArray(data) ? data : [];
    } catch (e) {
        return [];
    }
}

export async function actualizarIntervaloNotificacion(cuil, dias) {
    await apiClient.patch(`${BASE}/lista/${cuil}/intervalo`, { dias });
}

export async function obtenerNombreCliente(cuil) {
    try {
        const { data } = await apiClient.get(`${BASE}/cliente/${cuil}/nombre`);
        return data?.nombre || null;
    } catch (e) {
        return null;
    }
}

// Historial de sincronización del cliente (fecha/hora, estado, novedades, quién lo disparó) —
// alimenta el modal "Ver historial" del detalle de Seguimiento.
export async function obtenerHistorial(cuil, limit = 50) {
    try {
        const { data } = await apiClient.get(`${BASE}/cliente/${cuil}/historial`, { params: { limit } });
        return Array.isArray(data) ? data : [];
    } catch (e) {
        return [];
    }
}

// Preferencia de canal (in-app / mail) del usuario actual para este cliente puntual —
// default {recibirInApp: true, recibirEmail: true} si nunca la tocó.
// Seguir a un cliente: le llegan los avisos de ANSES de todos sus expedientes (los que tenga
// prendidos en Para aprobar → Mis avisos). Es la "estrella" del cliente (favorito en el backend).
export async function obtenerSeguido(cuil) {
    try {
        const { data } = await apiClient.get(`${BASE}/lista/${cuil}/favorito`);
        return Boolean(data?.favorito);
    } catch {
        return false;
    }
}

// Seguir o no un expediente puntual del cliente (V266): si no se sigue, no avisa ni cuenta en Para
// aprobar. Es la misma marca que la casilla "Seguir" del expediente en el detalle del trámite.
export async function seguirExpediente(cuil, nroExpediente, seguir) {
    await apiClient.put(`${BASE}/cliente/${cuil}/expedientes/seguir`, { nroExpediente, seguir });
}

export async function cambiarSeguido(cuil, seguir) {
    if (seguir) await apiClient.put(`${BASE}/lista/${cuil}/favorito`);
    else await apiClient.delete(`${BASE}/lista/${cuil}/favorito`);
}

export async function obtenerCanalPreferencia(cuil) {
    try {
        const { data } = await apiClient.get(`${BASE}/lista/${cuil}/canal`);
        return {
            recibirInApp: data?.recibirInApp !== false,
            recibirEmail: data?.recibirEmail !== false,
        };
    } catch (e) {
        return { recibirInApp: true, recibirEmail: true };
    }
}

export async function actualizarCanalPreferencia(cuil, { recibirInApp, recibirEmail }) {
    await apiClient.put(`${BASE}/lista/${cuil}/canal`, { recibirInApp, recibirEmail });
}

export default {
    listarExpedientes,
    obtenerExpedientesPorCliente,
    listarLista,
    agregarALista,
    quitarDeLista,
    actualizarCliente,
    actualizarIntervaloNotificacion,
    obtenerPrioridad,
    actualizarPrioridad,
    obtenerNombreCliente,
    obtenerHistorial,
    obtenerCanalPreferencia,
    actualizarCanalPreferencia,
};

// Página de control del robot (oct-2026): estado general, fallas agrupadas y últimas corridas.
export async function obtenerControlRobot() {
    const { data } = await apiClient.get(`${BASE}/control`);
    return data;
}
